import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Shield, ShieldCheck, ShieldAlert, ShieldX, Loader2, ExternalLink, Calendar } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/i18n/TranslationContext';
import { LanguageToggle } from '@/components/layout/LanguageToggle';

function getScoreInfo(score) {
  if (score >= 80) return { label: 'Compliant', color: 'text-vrs-green', bg: 'bg-vrs-green', icon: ShieldCheck, border: 'border-vrs-green/30' };
  if (score >= 60) return { label: 'At Risk', color: 'text-vrs-amber', bg: 'bg-vrs-amber', icon: ShieldAlert, border: 'border-vrs-amber/30' };
  return { label: 'Non-compliant', color: 'text-vrs-red', bg: 'bg-vrs-red', icon: ShieldX, border: 'border-vrs-red/30' };
}

function getMaturity(score) {
  if (score >= 91) return { level: 'Excellent', color: 'text-vrs-green', bg: 'bg-vrs-green/10', border: 'border-vrs-green/30' };
  if (score >= 71) return { level: 'High', color: 'text-vrs-cyan', bg: 'bg-vrs-cyan/10', border: 'border-vrs-cyan/30' };
  if (score >= 41) return { level: 'Moderate', color: 'text-vrs-amber', bg: 'bg-vrs-amber/10', border: 'border-vrs-amber/30' };
  return { level: 'Low', color: 'text-vrs-red', bg: 'bg-vrs-red/10', border: 'border-vrs-red/30' };
}

function formatDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
}

export default function PublicScorePage() {
  const { entityId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get(`/public/score/${entityId}`);
        setData(res.data);
      } catch { setData({ available: false, message: 'Failed to load profile.' }); }
      setLoading(false);
    };
    fetch();
  }, [entityId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Not available
  if (!data?.available) {
    return (
      <div className="min-h-screen bg-background vrs-grid relative flex items-center justify-center p-6">
        <Card className="max-w-md border-border/60 bg-card/80 backdrop-blur-sm">
          <CardContent className="p-8 text-center space-y-4">
            <Shield className="h-12 w-12 text-muted-foreground mx-auto" />
            <p className="text-lg font-semibold text-foreground">{t('score_not_available')}</p>
            <p className="text-sm text-muted-foreground">{data?.message || "This entity's compliance score is not publicly available."}</p>
            <div className="flex items-center justify-center gap-2 pt-4">
              <Shield className="h-4 w-4 text-primary" />
              <span className="text-xs text-muted-foreground">VRS - Vendor Risk Scanner</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const score = data.overall_score || 0;
  const si = getScoreInfo(score);
  const maturity = getMaturity(score);
  const SI = si.icon;
  const categories = data.category_scores || [];

  return (
    <div className="min-h-screen bg-background vrs-grid relative" data-testid="public-score-page">
      <div className="absolute top-4 right-4 z-50">
        <LanguageToggle />
      </div>
      <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(600px circle at 50% 20%, rgba(26,209,230,0.06), transparent 55%)' }} />
      <div className="max-w-2xl mx-auto px-6 py-12 relative">
        {/* Header */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 border border-primary/20">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <div className="text-center">
            <p className="text-lg font-bold tracking-tight text-foreground">VRS</p>
            <p className="text-[10px] text-muted-foreground">Vendor Risk Scanner</p>
          </div>
        </div>

        {/* Entity Name */}
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">{data.entity_name}</h1>
          {data.entity_url && (
            <a href={data.entity_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center justify-center gap-1 mt-1">
              {data.entity_url} <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>

        {/* Score Card */}
        <Card className={cn('border-border/60 mb-6', si.border)}>
          <CardContent className="p-8">
            <div className="flex flex-col items-center space-y-4">
              <SI className={cn('h-16 w-16', si.color)} />
              <p className={cn('text-6xl font-bold font-mono tabular-nums', si.color)}>{score}%</p>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className={cn('text-xs', si.color, si.border)}>{si.label}</Badge>
                <Badge variant="outline" className={cn('text-xs', maturity.color, maturity.border)}>{maturity.level} Maturity</Badge>
              </div>
              <Progress value={score} className={cn('h-3 w-full max-w-xs', `[&>div]:${si.bg}`)} />
              <p className="text-sm text-muted-foreground">{t('overall_compliance_score')}</p>
            </div>
          </CardContent>
        </Card>

        {/* Category Scores */}
        {categories.length > 0 && (
          <Card className="border-border/60 mb-6">
            <CardContent className="p-6 space-y-3">
              <p className="text-xs font-semibold tracking-wide uppercase text-muted-foreground text-center">{t('category_scores')}</p>
              {categories.map((cat, i) => {
                const catSi = getScoreInfo(cat.score);
                return (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-sm text-foreground w-36 truncate">{cat.category}</span>
                    <div className="flex-1">
                      <Progress value={cat.score} className={cn('h-2', `[&>div]:${catSi.bg}`)} />
                    </div>
                    <span className={cn('text-sm font-bold font-mono w-12 text-right', catSi.color)}>{cat.score}%</span>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Last scan date */}
        {data.last_scan_date && (
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground mb-6">
            <Calendar className="h-3 w-3" />
            Last scanned: {formatDate(data.last_scan_date)}
          </div>
        )}

        {/* Verified badge */}
        <div className="flex items-center justify-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <p className="text-sm font-medium text-foreground">{t('verified_by_vrs')}</p>
          <span className="text-xs text-muted-foreground">{"\u2014"} Vendor Risk Scanner</span>
        </div>
      </div>
    </div>
  );
}
